import React, { useEffect, useRef, useState } from 'react';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Box,
  Button,
  IconButton,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { withTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import DecimalField from '../shared/DecimalField';
import axios from 'axios';
import { useUserStore } from '../../store';
import { useCompanyLookup } from '../../hooks/useCompanyLookup';
import { BusinessIdLookupField, EInvoiceLookupButton } from '../shared/CompanyLookupFields';
import { hasCustomerIdentity } from '../../utils/customer';

function CustomerDialog({ t, addCreatedCustomer, updateCustomer, open, handleClose, customer, onDelete }) {
  const [formData, setFormData] = useState(customer || {});
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { currentOrganization } = useUserStore();
  const orgPaymentTermsDays = currentOrganization?.default_payment_terms_days;
  const amiliEnabled =
    (currentOrganization?.amili_enabled || false) && currentOrganization?.amili_status === 'active';

  const [isDirty, setIsDirty] = useState(false);

  const handleFormChanged = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setIsDirty(true);
  };

  const handleCheckboxChanged = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
    setIsDirty(true);
  };

  // The field keeps the Y-tunnus in "1234567-8" shape while typing; a changed ID
  // also hides any lookup banner that described the previous one.
  const handleBusinessIdChanged = (businessId) => {
    setFormData((prev) => ({ ...prev, business_id: businessId }));
    setIsDirty(true);
  };

  const companyLookup = useCompanyLookup({
    t,
    customer: formData,
    applyUpdates: (updates) => {
      setFormData((prev) => ({ ...prev, ...updates }));
      setIsDirty(true);
    },
  });
  const { resetLookups } = companyLookup;

  useEffect(() => {
    setFormData(customer || { type: 'individual' });
    setErrors({});
    setShowDeleteConfirm(false);
    resetLookups();
    setIsDirty(false);
  }, [customer, open, resetLookups]);

  // Unsaved changes protection for dialog close (backdrop click / Escape)
  const handleSubmitRef = useRef(null);
  const { guardedOnClose, UnsavedChangesDialog } = useUnsavedChanges({
    hasChanges: isDirty && open,
    onSave: () => handleSubmitRef.current?.(),
    mode: 'dialog',
    onClose: handleClose,
  });

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    if (customer && onDelete) {
      await onDelete(customer.id);
      handleClose();
    }
  };

  // Returns false when the save failed, so useUnsavedChanges knows to keep the dialog
  // open. Must not throw: this is also the button's onClick, where a rejected promise
  // nobody awaits becomes an unhandled rejection (VERSTAANI-FRONTEND-G).
  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});
    // An emptied maksuehto means "use the organization default", which the API stores as null.
    // Leave a missing key alone: omitting it keeps whatever the customer already has.
    const payload =
      formData.payment_terms_days === '' ? { ...formData, payment_terms_days: null } : formData;
    try {
      if (customer) {
        const response = await axios.put(`/api/customers/${customer.id}/`, payload);
        updateCustomer(response.data);
        handleClose();
      } else {
        const response = await axios.post('/api/customers/', payload);
        addCreatedCustomer(response.data);
        handleClose();
      }
      return true;
    } catch (error) {
      console.error('There was an error with the customer!', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
      return false;
    }
  };

  handleSubmitRef.current = handleSubmit;

  // A name, a phone number or an email is needed (the API refuses a customer with none)
  const identityMissing = !hasCustomerIdentity(formData);

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={guardedOnClose}
      maxWidth="sm"
      fullWidth
      data-testid="customer-dialog"
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {customer ? t('Edit Customer') : t('Add Customer')}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
        {/* Customer Type Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('Customer Type')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
            <Box
              data-testid="customer-type-individual"
              onClick={() => setFormData({ ...formData, type: 'individual' })}
              sx={{
                flex: 1,
                p: { xs: 1.5, sm: 2 },
                border: 2,
                borderRadius: '12px',
                borderColor: formData.type === 'individual' ? 'secondary.main' : 'divider',
                bgcolor: formData.type === 'individual' ? 'rgba(49, 130, 206, 0.08)' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '&:hover': {
                  borderColor: formData.type === 'individual' ? 'secondary.main' : 'secondary.light',
                  bgcolor: formData.type === 'individual' ? 'rgba(49, 130, 206, 0.08)' : 'rgba(49, 130, 206, 0.04)',
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: '10px',
                  bgcolor: formData.type === 'individual' ? 'secondary.main' : 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: formData.type === 'individual' ? 'white' : 'text.secondary',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
              >
                <PersonIcon fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{t('Individual')}</Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('Private person')}
                </Typography>
              </Box>
            </Box>

            <Box
              data-testid="customer-type-company"
              onClick={() => setFormData({ ...formData, type: 'company' })}
              sx={{
                flex: 1,
                p: { xs: 1.5, sm: 2 },
                border: 2,
                borderRadius: '12px',
                borderColor: formData.type === 'company' ? 'secondary.main' : 'divider',
                bgcolor: formData.type === 'company' ? 'rgba(49, 130, 206, 0.08)' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '&:hover': {
                  borderColor: formData.type === 'company' ? 'secondary.main' : 'secondary.light',
                  bgcolor: formData.type === 'company' ? 'rgba(49, 130, 206, 0.08)' : 'rgba(49, 130, 206, 0.04)',
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: '10px',
                  bgcolor: formData.type === 'company' ? 'secondary.main' : 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: formData.type === 'company' ? 'white' : 'text.secondary',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
              >
                <BusinessIcon fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{t('Company')}</Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('Business customer')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Error messages */}
        {errors.non_field_errors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.non_field_errors.join(', ')}
          </Alert>
        )}

        {/* Individual Details */}
        {formData.type === 'individual' && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
              {t('Personal Details')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                id="first_name"
                name="first_name"
                label={t('First Name')}
                onChange={handleFormChanged}
                fullWidth
                value={formData.first_name || ''}
                sx={textFieldStyles}
              />
              <TextField
                id="last_name"
                name="last_name"
                label={t('Last Name')}
                onChange={handleFormChanged}
                fullWidth
                value={formData.last_name || ''}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        )}

        {/* Company Details */}
        {formData.type === 'company' && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
              {t('Company Details')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Business ID first: it unlocks the YTJ autofill for the rest */}
              <BusinessIdLookupField
                t={t}
                lookup={companyLookup}
                id="business_id"
                value={formData.business_id}
                onChange={handleBusinessIdChanged}
                error={!!errors.business_id}
                helperText={errors.business_id?.[0]}
                sx={textFieldStyles}
              />

              <TextField
                id="company_name"
                name="company_name"
                label={t('Company Name')}
                onChange={handleFormChanged}
                fullWidth
                value={formData.company_name || ''}
                error={!!errors.company_name}
                helperText={errors.company_name?.[0]}
                sx={textFieldStyles}
              />
              <TextField
                id="contact_person"
                name="contact_person"
                label={t('Contact Person')}
                onChange={handleFormChanged}
                fullWidth
                value={formData.contact_person || ''}
                error={!!errors.contact_person}
                helperText={errors.contact_person?.[0]}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        )}

        {/* Basic Information */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('Basic Information')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                id="email"
                name="email"
                label={t('Email Address')}
                type="email"
                onChange={handleFormChanged}
                fullWidth
                value={formData.email || ''}
                error={!!errors.email}
                helperText={errors.email?.[0] || (identityMissing ? t('customer.identityHint') : '')}
                sx={textFieldStyles}
              />
              <TextField
                id="phone"
                name="phone"
                label={t('Phone Number')}
                type="tel"
                onChange={handleFormChanged}
                fullWidth
                value={formData.phone || ''}
                error={!!errors.phone}
                helperText={errors.phone?.[0] || (identityMissing ? t('customer.identityHint') : '')}
                inputProps={{ maxLength: 20 }}
                sx={textFieldStyles}
              />
            </Box>
            <LanguageSelect
              fullWidth
              sx={textFieldStyles}
              name="preferred_language"
              value={formData.preferred_language || ''}
              label={t('customer.preferredLanguage', 'Customer Language')}
              onChange={handleFormChanged}
              allowEmpty
              emptyLabel={t('Use organization default')}
            />
            {currentOrganization?.service_reminders_available && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.reminders_opt_out || false}
                    onChange={handleCheckboxChanged}
                    name="reminders_opt_out"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px' }}>
                    {t('service_reminders.customer_opt_out')}
                  </Typography>
                }
              />
            )}
            <TextField
              id="address_street"
              name="address_street"
              label={t('Street Address')}
              onChange={handleFormChanged}
              fullWidth
              value={formData.address_street || ''}
              error={!!errors.address_street}
              helperText={errors.address_street?.[0]}
              sx={textFieldStyles}
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                id="address_postal_code"
                name="address_postal_code"
                label={t('Postal Code')}
                onChange={handleFormChanged}
                value={formData.address_postal_code || ''}
                error={!!errors.address_postal_code}
                helperText={errors.address_postal_code?.[0]}
                sx={{ ...textFieldStyles, flex: 1 }}
              />
              <TextField
                id="address_city"
                name="address_city"
                label={t('City')}
                onChange={handleFormChanged}
                value={formData.address_city || ''}
                error={!!errors.address_city}
                helperText={errors.address_city?.[0]}
                sx={{ ...textFieldStyles, flex: 2 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Default Buyer's Reference (Viittenne) - for company customers */}
        {formData.type === 'company' && (
          <Box sx={{ mb: 3 }}>
            <TextField
              id="default_buyers_reference"
              name="default_buyers_reference"
              label={t('Default buyers reference')}
              onChange={handleFormChanged}
              fullWidth
              value={formData.default_buyers_reference || ''}
              inputProps={{ maxLength: 35 }}
              sx={textFieldStyles}
            />
          </Box>
        )}

        {/* E-Invoice Settings - for company customers */}
        {formData.type === 'company' && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ReceiptIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>
                {t('E-Invoice Settings')}
              </Typography>
            </Box>

            <EInvoiceLookupButton t={t} lookup={companyLookup} sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  id="verkkolasku_address"
                  name="verkkolasku_address"
                  label={t('E-Invoice Address')}
                  onChange={handleFormChanged}
                  fullWidth
                  value={formData.verkkolasku_address || ''}
                  placeholder="003712345678 / FI37..."
                  sx={textFieldStyles}
                />
                <TextField
                  id="verkkolasku_operator"
                  name="verkkolasku_operator"
                  label={t('Operator Code')}
                  onChange={handleFormChanged}
                  fullWidth
                  value={formData.verkkolasku_operator || ''}
                  placeholder="DABAFIHH"
                  sx={textFieldStyles}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.prefers_verkkolasku || false}
                    onChange={handleCheckboxChanged}
                    name="prefers_verkkolasku"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px' }}>
                    {t('Prefers e-invoice (verkkolasku)')}
                  </Typography>
                }
              />
              {amiliEnabled && (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.amili_excluded || false}
                        onChange={handleCheckboxChanged}
                        name="amili_excluded"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px' }}>
                        {t('Disable Amili payment monitoring for this customer')}
                      </Typography>
                    }
                  />
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary', ml: 4, mt: -0.5 }}>
                    {formData.amili_excluded && !formData.business_id?.trim()
                      ? t('amiliExcludedNeedsBusinessId')
                      : t('amiliExcludedHelp')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Internal note: shown on the customer's work orders, never to the customer */}
        <Box sx={{ mb: 3 }}>
          <TextField
            id="internal_note"
            name="internal_note"
            label={t('internalNote.label')}
            onChange={handleFormChanged}
            fullWidth
            multiline
            minRows={2}
            maxRows={8}
            value={formData.internal_note || ''}
            error={!!errors.internal_note}
            helperText={errors.internal_note?.[0] || t('internalNote.help')}
            sx={textFieldStyles}
          />
        </Box>

        {/* Payment terms (maksuehto) - empty uses the organization default */}
        <Box sx={{ mb: 3 }}>
          <DecimalField
            id="payment_terms_days"
            name="payment_terms_days"
            label={t('customer.paymentTerms')}
            maxDecimals={0}
            max={365}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, payment_terms_days: value }));
              setIsDirty(true);
            }}
            fullWidth
            value={formData.payment_terms_days}
            error={!!errors.payment_terms_days}
            helperText={
              errors.payment_terms_days?.[0] ||
              (orgPaymentTermsDays != null
                ? t('customer.paymentTermsHelp', { days: orgPaymentTermsDays })
                : t('Use organization default'))
            }
            InputProps={{ endAdornment: <InputAdornment position="end">{t('days')}</InputAdornment> }}
            sx={textFieldStyles}
          />
        </Box>

        {/* Pricing Settings */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {t('Pricing Settings')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              id="parts_margin_percent"
              name="parts_margin_percent"
              label={t('Parts Margin %')}
              type="number"
              onChange={handleFormChanged}
              fullWidth
              value={formData.parts_margin_percent || ''}
              placeholder={t('Use organization default')}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={textFieldStyles}
            />
            <TextField
              id="discount_percent"
              name="discount_percent"
              label={t('Discount %')}
              type="number"
              onChange={handleFormChanged}
              fullWidth
              value={formData.discount_percent || ''}
              placeholder={t('No discount')}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={textFieldStyles}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
            <TextField
              id="parts_discount_percent"
              name="parts_discount_percent"
              label={t('Parts Discount %')}
              type="number"
              onChange={handleFormChanged}
              fullWidth
              value={formData.parts_discount_percent || ''}
              placeholder={t('No discount')}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={textFieldStyles}
            />
            <TextField
              id="jobs_discount_percent"
              name="jobs_discount_percent"
              label={t('Jobs Discount %')}
              type="number"
              onChange={handleFormChanged}
              fullWidth
              value={formData.jobs_discount_percent || ''}
              placeholder={t('No discount')}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={textFieldStyles}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
        {/* Delete button - only shown when editing */}
        {customer && onDelete ? (
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleDelete}
            sx={{
              borderRadius: '10px',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {showDeleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
          </Button>
        ) : (
          <Box />
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              borderRadius: '10px',
              px: 3,
              color: 'text.secondary',
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            data-testid="submit-customer-button"
            onClick={handleSubmit}
            sx={{
              borderRadius: '10px',
              px: 3,
              bgcolor: 'secondary.main',
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {customer ? t('Save Changes') : t('Add Customer')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    {UnsavedChangesDialog}
  </>
  );
}

export default withTranslation()(CustomerDialog);
